<template>
  <div class="fullscreen-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-title">
        <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
          <div>
            <h3>Quick Scan Geographic Performance Overview - {{ businessInfo.business_name || 'Business' }}</h3>
            <div v-if="isLoading" class="loading-text">Loading scan data...</div>
          </div>
          <button class="export-btn" @click="showExportModal = true">
            <span class="btn-icon">📊</span>
            Export
          </button>
        </div>
      </div>
      
      <div class="legend">
        <div class="legend-item">
          <div class="legend-color rank-1-3"></div>
          <span>Positions 1-3</span>
        </div>
        <div class="legend-item">
          <div class="legend-color rank-4-7"></div>
          <span>Positions 4-7</span>
        </div>
        <div class="legend-item">
          <div class="legend-color rank-8-15"></div>
          <span>Positions 8-15</span>
        </div>
        <div class="legend-item">
          <div class="legend-color rank-16-plus"></div>
          <span>Position 16+</span>
        </div>
        <div class="legend-item">
          <div class="legend-color not-ranked"></div>
          <span>Not Ranked / Pending</span>
        </div>
      </div>

      <!-- Business Info Summary -->
      <div v-if="businessInfo.business_name" class="business-info-summary">
        <div class="business-info-content">
          <div style="display: flex; gap: 24px; align-items: flex-start;">
            <div>
              <div style="font-weight: 600; font-size: 1rem; color: #19233A; margin-bottom: 4px;">
                {{ businessInfo.business_name }}
              </div>
              <div style="font-size: 0.875rem; color: #757B89;">
                {{ businessInfo.address }}
              </div>
            </div>
            <!-- Competitors Display -->
            <div v-if="competitors && competitors.length > 0" style="border-left: 1px solid #E5E7EB; padding-left: 24px;">
              <div style="font-size: 0.75rem; color: #9CA3AF; text-transform: uppercase; margin-bottom: 6px;">Competitors</div>
              <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                <span 
                  v-for="(competitor, index) in competitors" 
                  :key="index"
                  style="font-size: 0.875rem; color: #4f46e5; font-weight: 500;"
                >
                  {{ competitor.name }}<span v-if="index < competitors.length - 1">,</span>
                </span>
              </div>
            </div>
          </div>
          <div style="display: flex; gap: 16px; align-items: center; flex-wrap: wrap;">
            <div style="text-align: center;">
              <div style="font-size: 1.25rem; font-weight: 700; color: #4f46e5;">{{ totalProcessItems }}</div>
              <div style="font-size: 0.75rem; color: #757B89; text-transform: uppercase;">Grid Points</div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 1.25rem; font-weight: 700; color: #10b981;">{{ completedCount }}</div>
              <div style="font-size: 0.75rem; color: #757B89; text-transform: uppercase;">Completed</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Google Maps Container with Grid Overlay -->
    <div class="map-container">
      <div id="googleMap" ref="mapContainer" class="map-element"></div>
      
      <!-- Business Listing Panel -->
      <transition name="slide-fade">
        <div v-if="showBusinessPanel" class="business-panel">
            <div class="business-panel-header">
              <div class="business-panel-title">
                <!-- <span>Searched results for this location:</span> -->
                <span>Searched results for this keyword:</span>
                <button @click="closeBusinessPanel" class="close-panel-btn">×</button>
              </div>
              <!-- <div class="business-panel-coords">
                Coordinates: {{ selectedLocation.lat }}, {{ selectedLocation.lng }}
              </div> -->
              <div class="business-panel-keyword">
                <span class="keyword-badge">{{ selectedKeyword }}</span>
              </div>
            </div>
            <div class="business-panel-content">
              <div v-if="businessListings.length === 0" class="no-results">
                <p>No businesses found at this location</p>
              </div>
              <div v-else class="business-list">
                <div 
                  v-for="(business, index) in businessListings" 
                  :key="index" 
                  class="business-item"
                  :class="{ 'competitor-item': isCompetitor(business) }"
                >
                  <div class="business-rank">{{ index + 1 }}</div>
                  <div class="business-details">
                    <div class="business-name">{{ business.name }}</div>
                    <div class="business-address">{{ business.address }}</div>
                    <div class="business-meta">
                      <div class="business-rating" v-if="business.rating">
                        <span class="rating-stars">
                          <span v-for="n in 5" :key="n" class="star" :class="{ 'filled': n <= Math.floor(business.rating) }">★</span>
                        </span>
                        <span class="rating-text">{{ business.rating.toFixed(1) }} ({{ business.reviews }})</span>
                      </div>
                      <div v-else class="no-rating">No ratings</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </transition>
      
      <!-- Custom Map Controls -->
      <div class="map-controls">
        <button class="map-control-btn" @click="zoomIn" title="Zoom In">+</button>
        <button class="map-control-btn" @click="zoomOut" title="Zoom Out">−</button>
        <button class="map-control-btn" @click="resetView" title="Reset View">⌂</button>
      </div>
      
      <!-- Grid Info Panel -->
      <div class="grid-info-panel">
        <div class="grid-info-title">
          Grid: {{ totalProcessItems }} locations ({{ gridSize }}×{{ gridSize }})
        </div>
        <div class="grid-info-subtitle">
          Zoom: {{ currentZoom }} • Status: {{ completedCount }}/{{ totalProcessItems }} completed
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <div v-if="showExportModal" class="modal" @click="closeExportModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">📊 Export Report</h3>
          <button class="close-modal" @click="closeExportModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Export Format:</label>
            <select v-model="exportFormat">
              <option value="csv">CSV Data</option>
              <option value="image">PNG Image (Map Screenshot)</option>
              <option value="pdf">PDF Report</option>
            </select>
          </div>
          
          <div class="export-description">
            <p v-if="exportFormat === 'csv'">
              Export all scan data including rankings, coordinates, and business details to a CSV file.
            </p>
            <p v-if="exportFormat === 'image'">
              Capture a screenshot of the map with all grid points and markers.
            </p>
            <p v-if="exportFormat === 'pdf'">
              Generate a professional PDF report with performance analytics, charts, and strategic recommendations.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel" @click="closeExportModal">Cancel</button>
          <button class="modal-btn primary" @click="handleExport">
            <span v-if="isExporting">Exporting...</span>
            <span v-else>Export</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import html2canvas from 'html2canvas'
import { generateQuickScanPDF } from '@/utils/pdfGenerator'

export default {
  metaInfo () {
    return {
      title: "Quick Scan | " + this.$appName
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      currentZoom: 14,
      scanData: null, 
      processItems: [], 
      businessInfo: {}, 
      keywords: [], 
      gridSize: 3, 
      mapCenter: { lat: 28.5887354, lng: 77.0829215 },
      isLoading: false,
      pollTimer: null,
      // API key will be fetched from store dynamically
      gridRectangle: null,
      gridOverlays: [],
      showBusinessPanel: false,
      businessListings: [],
      selectedLocation: { lat: '', lng: '' },
      selectedKeyword: '',
      competitors: [], // Store competitors data
      // Export modal
      showExportModal: false,
      exportFormat: 'csv',
      isExporting: false
    }
  },
  computed: {
    totalProcessItems() {
      return this.processItems.length;
    },
    completedCount() {
      return this.processItems.filter(item => item.status === 'completed').length;
    },
  },
  async mounted() {
    // First, fetch the API key from the store if not already loaded
    await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');
    this.loadGoogleMaps();
  },
  methods: {
    loadGoogleMaps() {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.initMap();
        return;
      }

      // Load Google Maps API
      const script = document.createElement('script');
      const userApiKey = this.$store.getters['apiKeys/googleMapsKey'];
      if (!userApiKey) {
        console.error('No Google Maps API key configured');

        // Check if in embed mode
        if (this.$store.getters['embed/isEmbedMode']) {
          // In embed mode, show simplified dialog and redirect to scans
          this.$swal({
            icon: 'info',
            title: 'API Key Not Configured',
            text: 'Google Maps API key is not configured.',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then(() => {
            this.$router.push('/scans');
          });
        } else {
          // Normal mode - show full settings option
          this.$swal({
            icon: 'error',
            title: 'API Key Missing',
            text: 'Google Maps API key not configured. Please configure it in Settings.',
            confirmButtonText: 'OK'
          });
        }
        return;
      }
      script.src = `https://maps.googleapis.com/maps/api/js?key=${userApiKey}&callback=initGoogleMap`;
      script.async = true;
      script.defer = true;
      
      // Set up global callback
      window.initGoogleMap = () => {
        this.initMap();
      };
      
      document.head.appendChild(script);
    },

    initMap() {
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        zoom: this.currentZoom,
        center: this.mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        gestureHandling: 'cooperative',
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Listen for zoom changes
      this.map.addListener('zoom_changed', () => {
        this.currentZoom = this.map.getZoom();
        // Recreate grid overlay when zoom changes
        setTimeout(() => this.createGridOverlay(), 300);
      });

      // Listen for bounds changes to update grid overlay
      this.map.addListener('bounds_changed', () => {
        setTimeout(() => this.createGridOverlay(), 100);
      });

      // Create markers if data is already loaded
      // if (this.processItems.length > 0) {
      //   this.createMarkers();
      // }
        this.fetchScanData();
        this.startPolling();
    },

    createMarkers() {
      // Clear existing markers
      this.clearMarkers();

      this.processItems.forEach((item, index) => {
        if (item.latitude && item.longitude) {
          const position = new google.maps.LatLng(item.latitude, item.longitude);
          const marker = this.createMarker(position, item, index);
          this.markers.push(marker);
        }
      });
      
      // Create grid overlay after markers are placed
      this.createGridOverlay();
    },

    createMarker(position, item, index) {
      const isCompleted = item.status === 'completed';
      let displayText = '?';
      if (isCompleted) {
        if (item.rank === 0) {
          displayText = '20+';
        } else if (item.rank) {
          displayText = item.rank.toString();
        } else {
          displayText = 'NR';
        }
      }
      const color = isCompleted ? this.getRankColor(item.rank) : '#757B89';

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, displayText))}`,
          scaledSize: new google.maps.Size(32, 48),
          anchor: new google.maps.Point(16, 48) // Anchor at the bottom point of the pin
        },
        title: `${item.keyword || 'Unknown'} - ${isCompleted ? `Rank ${item.rank || 'NR'}` : 'Pending'}`
      });

      // Add click listener for detailed info (optional)
      marker.addListener('click', () => {
        this.showMarkerInfo(item, position);
      });

      return marker;
    },

    createMarkerSVG(color, displayText) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <!-- Outer pin shape with shadow -->
          <ellipse cx="16" cy="46" rx="8" ry="2" fill="#000000" opacity="0.2"/>
          <!-- Pin shape path -->
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 32 16 32s16-20 16-32C32 7.2 24.8 0 16 0z" 
                fill="${color}" 
                stroke="#FFFFFF" 
                stroke-width="1.5"/>
          <!-- Inner circle for text background -->
          <circle cx="16" cy="16" r="11" fill="#FFFFFF" opacity="0.95"/>
          <!-- Text -->
          <text x="16" y="20" text-anchor="middle" fill="${color}" font-family="Rubik, Arial, sans-serif" font-size="11" font-weight="700">${displayText}</text>
        </svg>
      `;
    },

    getRankColor(rank) {
      if (rank === 'NR') return '#757B89'; 
      if (!rank) return '#ef4444';
      const numRank = parseInt(rank);
      if (numRank <= 3) return '#10b981';      
      if (numRank <= 7) return '#4f46e5';      
      if (numRank <= 15) return '#FFB443';    
      if (numRank <= 0) return '#ef4444';     
      return '#757B89';                       
    },

    showMarkerInfo(item, position) {
      // Don't show info window popup above marker - only show the side panel
      // Close any existing info window
      if (this.infoWindow) {
        this.infoWindow.close();
      }
      
      // Show business panel with listings
      this.showBusinessListings(item, position);
    },

    clearMarkers() {
      this.markers.forEach(marker => {
        marker.setMap(null);
      });
      this.markers = [];
    },

    createGridOverlay() {
      if (!this.map) return;
      
      const bounds = this.map.getBounds();
      if (!bounds) {
        // If bounds not ready, wait and try again
        google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => this.createGridOverlay());
        return;
      }

      // Clear existing overlays
      this.clearGridOverlays();

      // If no markers, don't create overlay
      if (this.markers.length === 0) return;

      // Calculate bounds that encompass all markers
      const markerBounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => {
        markerBounds.extend(marker.getPosition());
      });

      // Add padding around the markers
      const ne = markerBounds.getNorthEast();
      const sw = markerBounds.getSouthWest();
      
      // Calculate minimal padding (about 2% of the bounds) - just enough to not overlap markers
      const latPadding = (ne.lat() - sw.lat()) * 0.02;
      const lngPadding = (ne.lng() - sw.lng()) * 0.02;
      
      // Create the blue rectangle overlay
      const backgroundBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(sw.lat() - latPadding, sw.lng() - lngPadding),
        new google.maps.LatLng(ne.lat() + latPadding, ne.lng() + lngPadding)
      );

      this.gridRectangle = new google.maps.Rectangle({
        bounds: backgroundBounds,
        fillColor: '#4f46e5',
        fillOpacity: 0.15,
        strokeColor: '#4f46e5',
        strokeOpacity: 0.3,
        strokeWeight: 2,
        map: this.map
      });
    },

    clearGridOverlays() {
      // Clear the main grid rectangle
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null);
        this.gridRectangle = null;
      }
      
      // Clear any additional grid overlays
      this.gridOverlays.forEach(overlay => {
        overlay.setMap(null);
      });
      this.gridOverlays = [];
    },

    zoomIn() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() + 1);
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },

    zoomOut() {
      if (this.map) {
        this.map.setZoom(this.map.getZoom() - 1);
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },

    resetView() {
      if (this.map) {
        this.map.setZoom(14);
        this.map.setCenter(this.mapCenter);
        setTimeout(() => this.createGridOverlay(), 300);
      }
    },

    async fetchScanData() {
      this.isLoading = true;
      try {
        const id = this.$route.params.id;
        const res = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/get-grid-points/${id}`
        );

        if (res.data.success) {
         
          this.scanData = res.data.data;
          let firstScanItem = this.scanData.ScanItems[0];
          this.businessInfo = res.data.data;
          
          // Store competitors if they exist
          this.competitors = this.scanData.competitors || [];
          if (firstScanItem && firstScanItem.latitude && firstScanItem.longitude) {
            this.mapCenter = {
              lat: Number(firstScanItem.latitude),
              lng: Number(firstScanItem.longitude)
            };

            // ✅ If map is already initialized, recenter it
            if (this.map) {
              this.map.setCenter(this.mapCenter);
            }
          }

          // Set processItems
          this.processItems = firstScanItem.ProcessItems || [];

          // Create markers after data is loaded
          if (this.map) {
            this.createMarkers();
            this.fitMapToMarkers();
          }

          // Stop polling if completed
          if (this.scanData.status === 'completed') {
            this.stopPolling();
            this.isLoading = false;
          } else {
            this.isLoading = true;
          }

        } else {
          // Something went wrong
          this.stopPolling();
        }
      } catch (err) {
        console.error(err);
        // Failed to fetch scan data
        this.stopPolling();
      }
    },

    fitMapToMarkers() {
      if (!this.map || this.processItems.length === 0) return;

      const bounds = new google.maps.LatLngBounds();

      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          bounds.extend(new google.maps.LatLng(item.latitude, item.longitude));
        }
      });

      this.map.fitBounds(bounds);
    },

    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.fetchScanData();
      }, 5000);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    // Method to update markers when data changes
    updateMarkers(newProcessItems) {
      this.processItems = newProcessItems;
      if (this.map) {
        this.createMarkers();
      }
    },
    
    // Check if a business is a competitor
    isCompetitor(business) {
      if (!business || !business.place_id) return false;
      return this.competitors.some(competitor => 
        competitor.place_id === business.place_id || 
        (competitor.business_data && competitor.business_data.place_id === business.place_id)
      );
    },

    // Show business listings panel
    showBusinessListings(item, position) {
      this.selectedLocation = {
        lat: position.lat().toFixed(6),
        lng: position.lng().toFixed(6)
      };
      this.selectedKeyword = item.keyword || 'Unknown';
      
      // Generate mock business data based on the item
      // In production, this would fetch from your API
      this.businessListings = this.generateBusinessListings(item);
      this.showBusinessPanel = true;
      
    },
    
    // Generate business listings from item.result
    generateBusinessListings(item) {
    
      
      // Parse the result if it's a string
      let resultData = item.result;
      if (typeof resultData === 'string') {
        try {
          resultData = JSON.parse(resultData);
        } catch (e) {
          console.error('Failed to parse result data:', e);
          return [];
        }
      }
      
      // Map the business data from the result
      const businesses = resultData.map((business, index) => {
        return {
          name: business.name || 'Unknown Business',
          address: business.formatted_address || business.vicinity || 'Address not available',
          rating: business.rating || null,
          reviews: business.user_ratings_total || 0,
          types: business.types || [],
          place_id: business.place_id || null,
          isOpen: business.opening_hours ? business.opening_hours.open_now : null
        };
      });
      
      // Return top 20 businesses (or however many are available)
      return businesses.slice(0, Math.min(20, businesses.length));
    },

    // Close business panel
    closeBusinessPanel() {
      this.showBusinessPanel = false;
      this.businessListings = [];
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    },

    // Export modal methods
    closeExportModal() {
      this.showExportModal = false;
    },

    async handleExport() {
      this.isExporting = true;
      try {
        if (this.exportFormat === 'csv') {
          await this.exportToCSV();
        } else if (this.exportFormat === 'image') {
          await this.exportToImage();
        } else if (this.exportFormat === 'pdf') {
          await this.exportToPDF();
        }
      } catch (error) {
        console.error('Export failed:', error);
        alert('Export failed. Please try again.');
      } finally {
        this.isExporting = false;
        this.closeExportModal();
      }
    },

    async exportToCSV() {
      // Prepare CSV data
      const csvData = [];
      
      // Headers
      const headers = [
        'Keyword',
        'Rank',
        'Latitude', 
        'Longitude',
        'Business Name',
        'Business Address',
        'Status',
        'Grid Point',
        'Distance from Business',
        'Scan Date'
      ];
      csvData.push(headers);

      // Data rows
      this.processItems.forEach((item, index) => {
        const rank = item.rank === 0 ? '20+' : (item.rank || 'Not Ranked');
        const row = [
          item.keyword || '',
          rank,
          item.latitude || '',
          item.longitude || '',
          this.businessInfo.business_name || '',
          this.businessInfo.address || '',
          item.status || '',
          `Point ${index + 1}`,
          this.calculateDistance(
            Number(this.businessInfo.latitude || this.mapCenter.lat),
            Number(this.businessInfo.longitude || this.mapCenter.lng),
            Number(item.latitude),
            Number(item.longitude)
          ).toFixed(2) + ' km',
          new Date(item.createdAt || Date.now()).toLocaleDateString()
        ];
        csvData.push(row);
      });

      // Convert to CSV string
      const csvContent = csvData.map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const businessName = this.businessInfo.business_name ? this.businessInfo.business_name.replace(/[^a-z0-9]/gi, '_') : 'scan';
      link.setAttribute('download', `quick_scan_${businessName}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async exportToImage() {
      // Close any open panels first
      this.closeBusinessPanel();
      
      // Wait for panels to close
      await new Promise(resolve => setTimeout(resolve, 300));

      // Create a temporary container that includes both header and map
      const captureContainer = document.createElement('div');
      captureContainer.style.position = 'absolute';
      captureContainer.style.left = '-9999px';
      captureContainer.style.top = '0';
      captureContainer.style.width = window.innerWidth + 'px';
      captureContainer.style.backgroundColor = '#ffffff';
      
      // Clone the header section
      const headerSection = document.querySelector('.header-section');
      const mapContainer = this.$refs.mapContainer;
      
      if (!headerSection || !mapContainer) {
        throw new Error('Required sections not found');
      }
      
      // Clone both sections
      const headerClone = headerSection.cloneNode(true);
      const mapClone = mapContainer.cloneNode(true);
      
      // Remove the export button from the cloned header
      const exportBtn = headerClone.querySelector('.export-btn');
      if (exportBtn) {
        exportBtn.remove();
      }
      
      // Set map height for the clone
      mapClone.style.height = mapContainer.offsetHeight + 'px';
      mapClone.style.position = 'relative';
      
      // Append both to capture container
      captureContainer.appendChild(headerClone);
      captureContainer.appendChild(mapClone);
      document.body.appendChild(captureContainer);

      // Wait for DOM to update
      await new Promise(resolve => setTimeout(resolve, 500));

      // Use html2canvas to capture the combined container
      const canvas = await html2canvas(captureContainer, {
        useCORS: true,
        allowTaint: true,
        scale: 2, // Higher quality
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: window.innerWidth,
        windowHeight: headerSection.offsetHeight + mapContainer.offsetHeight
      });
      
      // Remove temporary container
      document.body.removeChild(captureContainer);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const businessName = this.businessInfo.business_name ? this.businessInfo.business_name.replace(/[^a-z0-9]/gi, '_') : 'scan';
        link.setAttribute('download', `quick_scan_map_${businessName}_${new Date().toISOString().split('T')[0]}.png`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    },

    async exportToPDF() {
      try {
        // Close any open panels first
        this.closeBusinessPanel();
        
        // Wait for panels to close
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Capture both header and map together (same as exportToImage)
        let mapImageData = null;
        
        // Create a temporary container that includes both header and map
        const captureContainer = document.createElement('div');
        captureContainer.style.position = 'absolute';
        captureContainer.style.left = '-9999px';
        captureContainer.style.top = '0';
        captureContainer.style.width = window.innerWidth + 'px';
        captureContainer.style.backgroundColor = '#ffffff';
        
        // Clone the header section
        const headerSection = document.querySelector('.header-section');
        const mapContainer = this.$refs.mapContainer;
        
        if (headerSection && mapContainer && typeof html2canvas !== 'undefined') {
          try {
            // Clone both sections
            const headerClone = headerSection.cloneNode(true);
            const mapClone = mapContainer.cloneNode(true);
            
            // Remove the export button from the cloned header
            const exportBtn = headerClone.querySelector('.export-btn');
            if (exportBtn) {
              exportBtn.remove();
            }
            
            // Set map height for the clone
            mapClone.style.height = mapContainer.offsetHeight + 'px';
            mapClone.style.position = 'relative';
            
            // Append both to capture container
            captureContainer.appendChild(headerClone);
            captureContainer.appendChild(mapClone);
            document.body.appendChild(captureContainer);
            
            // Wait for DOM to update
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Use html2canvas to capture the combined container
            const canvas = await html2canvas(captureContainer, {
              useCORS: true,
              allowTaint: true,
              scale: 2, // Higher quality
              logging: false,
              backgroundColor: '#ffffff',
              windowWidth: window.innerWidth,
              windowHeight: headerSection.offsetHeight + mapContainer.offsetHeight
            });
            
            // Remove temporary container
            document.body.removeChild(captureContainer);
            
            mapImageData = canvas.toDataURL('image/png');
          } catch (error) {
            console.log('Could not capture map image:', error);
          }
        }
        
        // Calculate statistics from processItems
        const statsData = this.calculateStatistics();
        
        // Prepare scan data
        const scanData = {
          business_name: this.businessInfo.business_name || 'Business',
          address: this.businessInfo.address || '',
          results: this.processItems.map(item => ({
            keyword: item.keyword || '',
            rank: item.rank || 0,
            latitude: item.latitude,
            longitude: item.longitude,
            status: item.status,
            businessNames: item.businessNames || []
          })),
          keywords: this.businessInfo.keywords || '',
          grid_size: this.businessInfo.grid_size || '3x3',
          radius: this.businessInfo.radius || '1-2km',
          mapImage: mapImageData  // Pass the captured map image
        };
        
        // Generate PDF with map image
        const pdf = await generateQuickScanPDF(scanData, statsData);
        
        // Download PDF
        const businessName = this.businessInfo.business_name 
          ? this.businessInfo.business_name.replace(/[^a-z0-9]/gi, '_') 
          : 'scan';
        const fileName = `grid_rank_report_${businessName}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        pdf.save(fileName);
      } catch (error) {
        console.error('PDF generation failed:', error);
        throw error;
      }
    },

    calculateStatistics() {
      const stats = {
        total_keywords: [...new Set(this.processItems.map(item => item.keyword))].length,
        total_locations: this.processItems.length,
        total_combinations: this.processItems.length,
        completed_scans: this.completedCount,
        average_rank: 0,
        median_rank: 0,
        best_rank: null,
        worst_rank: null,
        ranked_count: 0,
        not_ranked_count: 0,
        coverage_percentage: 0,
        top_3_count: 0,
        top_10_count: 0,
        top_20_count: 0,
        top_3_percentage: 0,
        best_keyword: null,
        best_keyword_rank: null,
        worst_keyword: null,
        worst_keyword_rank: null
      };
      
      const rankedItems = this.processItems.filter(item => item.rank && item.rank > 0);
      const ranks = rankedItems.map(item => item.rank);
      
      if (ranks.length > 0) {
        // Calculate average
        stats.average_rank = ranks.reduce((a, b) => a + b, 0) / ranks.length;
        
        // Calculate median
        const sortedRanks = [...ranks].sort((a, b) => a - b);
        const mid = Math.floor(sortedRanks.length / 2);
        stats.median_rank = sortedRanks.length % 2 ? sortedRanks[mid] : (sortedRanks[mid - 1] + sortedRanks[mid]) / 2;
        
        // Best and worst ranks
        stats.best_rank = Math.min(...ranks);
        stats.worst_rank = Math.max(...ranks);
        
        // Position breakdown
        stats.top_3_count = ranks.filter(r => r <= 3).length;
        stats.top_10_count = ranks.filter(r => r <= 10).length;
        stats.top_20_count = ranks.filter(r => r <= 20).length;
        
        // Percentages
        stats.top_3_percentage = (stats.top_3_count / this.processItems.length) * 100;
        
        // Find best and worst keywords
        const bestItem = rankedItems.find(item => item.rank === stats.best_rank);
        const worstItem = rankedItems.find(item => item.rank === stats.worst_rank);
        
        if (bestItem) {
          stats.best_keyword = bestItem.keyword;
          stats.best_keyword_rank = bestItem.rank;
        }
        
        if (worstItem) {
          stats.worst_keyword = worstItem.keyword;
          stats.worst_keyword_rank = worstItem.rank;
        }
      }
      
      // Coverage statistics
      stats.ranked_count = rankedItems.length;
      stats.not_ranked_count = this.processItems.filter(item => !item.rank || item.rank === 0).length;
      stats.coverage_percentage = (stats.ranked_count / this.processItems.length) * 100;
      
      return stats;
    },

    // Helper function to calculate distance between two points
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Radius of the Earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c; // Distance in km
      return d;
    },

    deg2rad(deg) {
      return deg * (Math.PI/180);
    }
  },

  beforeUnmount() {
    this.stopPolling();
    this.clearMarkers();
    this.clearGridOverlays();
  }
}
</script>

<style scoped>
/* Fullscreen Container */
.fullscreen-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header Section */
.header-section {
  padding: 16px 20px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}

.header-title {
  width: 100%;
  margin-bottom: 16px;
}

.header-title h3 {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 1.125rem;
  color: #19233A;
  margin: 0;
}

.loading-text {
  font-size: 0.875rem;
  color: #757B89;
  margin-top: 4px;
}

/* Business Info Summary */
.business-info-summary {
  background: #f9fafb;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  margin-top: 12px;
}

.business-info-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

/* Map Container */
.map-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.map-element {
  width: 100%;
  height: 100%;
}

/* Map Controls */
.map-controls {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10;
}

.map-control-btn {
  width: 40px;
  height: 40px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 18px;
  color: #374151;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.map-control-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
  transform: scale(1.05);
}

/* Grid Info Panel */
.grid-info-panel {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 16px;
  border-radius: 8px;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.grid-info-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #19233A;
  margin-bottom: 4px;
}

.grid-info-subtitle {
  font-size: 0.75rem;
  color: #757B89;
}

/* Legend */
.legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #696969;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.rank-1-3 { background: #10b981; }
.rank-4-7 { background: #4f46e5; }
.rank-8-15 { background: #FFB443; }
.rank-16-plus { background: #ef4444; }
.not-ranked { background: #757B89; }

/* Map controls hover effects */
.map-control-btn:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .section {
    padding: 20px;
  }
  
  .legend {
    flex-direction: column;
    gap: 8px;
  }
  
  .google-maps-container {
    height: 400px !important;
  }
  
  .map-controls {
    top: 8px !important;
    right: 8px !important;
  }
  
  .map-control-btn {
    width: 36px !important;
    height: 36px !important;
    font-size: 16px !important;
  }
  
  .grid-info-panel {
    bottom: 8px !important;
    left: 8px !important;
    padding: 8px 12px !important;
  }
}

@media (max-width: 480px) {
  .google-maps-container {
    height: 350px !important;
  }
}

/* Export Button Styles */
.export-btn {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.export-btn:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.btn-icon {
  font-size: 16px;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px !important;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #19233a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.close-modal {
  background: none;
  border: none;
  font-size: 28px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-modal:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233a;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-group select:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.export-description {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.export-description p {
  margin: 0;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
}

.modal-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.modal-btn.cancel {
  background: #f3f4f6;
  color: #374151;
}

.modal-btn.cancel:hover {
  background: #e5e7eb;
}

.modal-btn.primary {
  background: #4f46e5;
  color: white;
}

.modal-btn.primary:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Business Panel Styles */
.business-panel {
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 420px;
  max-width: calc(100% - 32px);
  background: white;
  border-radius: 5px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 100;
  height: 70vh;  /* Changed to viewport height for better control */
  max-height: 650px;  /* Maximum limit */
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  overflow: hidden;  /* Ensure no overflow on main container */
}

.business-panel-header {
  padding: 12px 16px;  /* Reduced padding for smaller header */
  border-bottom: 1px solid #e5e7eb;
  background: #1e293b;
  color: white;
  border-radius: 6px 6px 0 0;
  flex-shrink: 0;  /* Prevent header from shrinking */
}

.business-panel-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.close-panel-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-panel-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.business-panel-coords {
  font-size: 13px;
  opacity: 0.8;
  margin-bottom: 12px;
}

.business-panel-keyword {
  margin-top: 8px;
}

.keyword-badge {
  display: inline-block;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  font-size: 13px;
  color: #ffffff;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.business-panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border-radius: 0 0 12px 12px;
  min-height: 0;  /* Important for flex child with overflow */
}

.business-list {
  padding: 8px;
}

.business-item {
  display: flex;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
  cursor: pointer;
  border-radius: 8px;
  margin: 4px;
}

.business-item:hover {
  background: #f9fafb;
}

.business-item.competitor-item {
  background: #4f46e5;
  color: white;
}

.business-item.competitor-item:hover {
  background: #4338ca;
}

.business-item.competitor-item .business-name {
  color: white;
}

.business-item.competitor-item .business-address {
  color: rgba(255, 255, 255, 0.8);
}

.business-item.competitor-item .rating-text,
.business-item.competitor-item .no-rating {
  color: rgba(255, 255, 255, 0.7);
}

.business-item.competitor-item .business-rank {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.business-item.competitor-item .star.filled {
  color: #fbbf24;
}

.business-item.competitor-item .star {
  color: rgba(255, 255, 255, 0.3);
}

.business-item:last-child {
  border-bottom: none;
}

.business-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
  color: #374151;
  flex-shrink: 0;
}

.business-details {
  flex: 1;
}

.business-name {
  font-weight: 600;
  font-size: 15px;
  color: #19233a;
  margin-bottom: 4px;
}

.business-address {
  font-size: 13px;
  color: #757b89;
  margin-bottom: 6px;
  line-height: 1.4;
}

.business-meta {
  margin-top: 6px;
}

.business-rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #d1d5db;
  font-size: 14px;
}

.star.filled {
  color: #fbbf24;
}

.rating-text {
  font-size: 12px;
  color: #6b7280;
}

.no-rating {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}

.no-results {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

/* Slide fade transition */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .header-section {
    padding: 12px 16px;
  }
  
  .header-title h3 {
    font-size: 1rem;
  }
  
  .business-panel {
    width: calc(100% - 32px);
    right: 16px;
    left: 16px;
    bottom: 8px;
    max-height: 40vh;
  }
  
  .business-panel-header {
    padding: 16px;
  }
  
  .business-item {
    padding: 12px;
  }
}
</style>